import React from 'react'

import styled from 'styled-components'

import { IconButton, Popover } from '@components/ui'

import { MENU_SIZES, MenuSizingOptions } from '../constants'

const MenuButton = styled(IconButton)`
  width: ${MENU_SIZES[MenuSizingOptions.SMALL]}px;
  height: ${MENU_SIZES[MenuSizingOptions.SMALL]}px;
  --css-focus-border-color: transparent;
`

const DropdownMenuContainer = styled.div<{
  $minWidth: number
  size?: 'small' | 'normal'
}>`
  min-width: ${({ $minWidth }) => $minWidth}px;
  display: flex;
  flex-direction: column;

  & > a,
  & > a > * {
    width: 100%;
  }
`

interface DropdownMenuButtonProps {
  children: (renderProps: {
    onCloseCallback: VoidFunction
    onClose: (event: MouseEvent | TouchEvent | React.MouseEvent<HTMLElement>) => void
  }) => NonNullable<React.ReactNode>
  className?: string
  icon: NonNullable<React.ReactNode>
  isContainerVisible: boolean
  placement: 'bottom-end' | 'bottom-start'
  transformOrigin: 'right top' | 'left top'
  width: number
}

export function DropdownMenuButton({
  children,
  className,
  icon,
  isContainerVisible,
  placement,
  width,
}: DropdownMenuButtonProps) {
  const [open, setOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  function onOpenChange(open: boolean) {
    setOpen(open)
  }

  React.useEffect(() => {
    // close menu when container is not visible
    if (!isContainerVisible && open) {
      setOpen(false)
    }
  }, [isContainerVisible, open])

  function onClose(event: MouseEvent | TouchEvent | React.MouseEvent<HTMLElement>) {
    if (containerRef.current?.contains(event.target as Nullable<Node>)) {
      return
    }
    setOpen(false)
  }

  function onCloseCallback() {
    setOpen(false)
  }

  return (
    <Popover
      onOpenChange={onOpenChange}
      open={open}
      placement={placement}
      trigger={<MenuButton className={className}>{icon}</MenuButton>}
      withoutPortal
      withoutScrollLock
    >
      <DropdownMenuContainer ref={containerRef} $minWidth={width}>
        {children({ onCloseCallback, onClose })}
      </DropdownMenuContainer>
    </Popover>
  )
}
